import { Component, Input, OnChanges, SimpleChanges, ViewChild, inject } from '@angular/core';
import { UtilService } from '../../../../services/util.service';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { TranslatePipe } from '@ngx-translate/core';

export interface InvoiceEvent {
  bin: string; binType: string; date: string; eventType: string;
  units: number; address: string; contract: string; reason: string; amount: string;
}

export interface SiteInvoice {
  siteName: string; monthlyFee: string; maintenanceFees: string;
  credits: number; units: number; total: string; expanded: boolean;
  events: InvoiceEvent[];
}

/** Keeps the original site reference (so toggle works) + a separate events list for display */
export interface FilteredSiteEntry {
  site: SiteInvoice;
  events: InvoiceEvent[];
}

@Component({
  selector: 'app-invoice-item',
  imports: [CommonModule, TranslatePipe, MatTableModule, MatSortModule],
  templateUrl: './invoice-item.html',
  styleUrl: './invoice-item.scss',
})
export class InvoiceItem implements OnChanges {
  @Input() site!: SiteInvoice;
  @Input() displayEvents: InvoiceEvent[] | null = null;

  @ViewChild(MatSort)
  set matSort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;
    }
  }

  displayedColumns = ['bin', 'binType', 'date', 'eventType', 'units', 'address', 'contract', 'amount'];
  dataSource = new MatTableDataSource<InvoiceEvent>([]);

  constructor(private utilService: UtilService) {
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'amount': return parseFloat(item.amount.replace(/[^0-9.]/g, '')) || 0;
        case 'units':  return item.units;
        default:       return (item as any)[property] ?? '';
      }
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['site'] || changes['displayEvents']) && this.site) {
      this.dataSource.data = this.displayEvents ?? this.site.events;
    }
  }

  toggle(): void { this.site.expanded = !this.site.expanded; }

  get eventTypeTotals(): { eventType: string; total: string }[] {
    const events = this.displayEvents ?? this.site?.events ?? [];
    const map = new Map<string, number>();
    let currency = '';

    for (const ev of events) {
      const rate = parseFloat(ev.amount) || 0;
      if (!currency && ev.amount) {
        currency = ev.amount.replace(/^[\d.,\s]+/, '').trim();
      }
      map.set(ev.eventType, (map.get(ev.eventType) || 0) + rate);
    }

    return Array.from(map.entries()).map(([eventType, total]) => ({
      eventType,
      total: `${this.utilService.roundAmount(total)} ${currency}`.trim()
    }));
  }
}
